import { Component, type ReactNode } from "react";
import { AlertTriangle, LogOut, RotateCcw } from "lucide-react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: { componentStack?: string | null } | null;
}

interface DiagSnapshot {
  pathname: string;
  userAgent: string;
  timestamp: string;
  hasLocalStorage: boolean;
  hasSession: boolean;
}

function takeDiagnosticSnapshot(): DiagSnapshot {
  let hasLocalStorage = false;
  try {
    hasLocalStorage = typeof localStorage !== "undefined" && Boolean(localStorage);
  } catch {
    hasLocalStorage = false;
  }
  let hasSession = false;
  try {
    if (hasLocalStorage) {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith("sb-") && k.endsWith("-auth-token")) {
          hasSession = true;
          break;
        }
      }
    }
  } catch {
    /* ignore */
  }
  return {
    pathname:
      typeof window !== "undefined" ? window.location.pathname : "unknown",
    userAgent:
      typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
    timestamp: new Date().toISOString(),
    hasLocalStorage,
    hasSession,
  };
}

// Tente de purger la session Supabase + flags locaux et redirige vers /.
// Utilise quand on suspecte que la session corrompue est la cause du
// crash (token JWT malforme, role invalide, donnees obsoletes).
async function safeSignOutAndRedirect() {
  // Marque le signout comme volontaire : l'utilisateur a clique « Reset
  // session », on ne veut pas que l'AuthContext lui envoie un magic
  // link de recovery juste apres (cf. PR #4b auto-recovery).
  try {
    const { markIntentionalSignout } = await import(
      "@/lib/intentionalSignout"
    );
    markIntentionalSignout();
  } catch {
    /* swallow */
  }
  try {
    const { supabase } = await import("@/lib/supabase");
    await supabase.auth.signOut();
  } catch {
    /* best effort */
  }
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && (k.startsWith("sb-") || k.startsWith("gsf:"))) {
        keysToRemove.push(k);
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
  window.location.href = "/";
}

// Styles inline volontaires : l'ErrorBoundary doit pouvoir s'afficher
// meme si la feuille de style globale a echoue (cas extreme : crash au
// demarrage du module CSS, FOUC long, mismatch Tailwind).
const FALLBACK_BG = "#0f0f23";
const FALLBACK_FG = "#f8fafc";
const FALLBACK_GOLD = "#d4af37";
const FALLBACK_RED = "#dc2626";
const FALLBACK_DIM = "#94a3b8";
const FALLBACK_BORDER = "#3a3a52";
const FALLBACK_PANEL = "#181825";

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(
    error: Error,
    errorInfo: { componentStack?: string | null },
  ) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] caught error", {
      name: error.name,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      diag: takeDiagnosticSnapshot(),
    });
    this.setState({ errorInfo });
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const error = this.state.error;
    const diag = takeDiagnosticSnapshot();

    return (
      <div
        style={{
          minHeight: "100vh",
          padding: "2rem",
          background: FALLBACK_BG,
          color: FALLBACK_FG,
          fontFamily:
            "'Inter', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ width: "100%", maxWidth: "720px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              marginBottom: "1rem",
            }}
          >
            <AlertTriangle size={28} color={FALLBACK_RED} />
            <h1
              style={{
                fontSize: "1.5rem",
                fontWeight: 600,
                margin: 0,
                letterSpacing: "-0.015em",
              }}
            >
              Erreur inattendue
            </h1>
          </div>

          <p
            style={{
              color: FALLBACK_DIM,
              fontSize: "0.9375rem",
              lineHeight: 1.55,
              marginBottom: "1.5rem",
            }}
          >
            La plateforme a rencontre une erreur. Cause la plus frequente :
            session corrompue ou token expire. Essayez d'abord{" "}
            <strong style={{ color: FALLBACK_FG }}>
              Reset session &amp; accueil
            </strong>{" "}
            avant un simple Reload.
          </p>

          {error && (
            <div
              style={{
                padding: "1rem",
                background: FALLBACK_PANEL,
                border: `1px solid ${FALLBACK_BORDER}`,
                borderRadius: "0.5rem",
                marginBottom: "1rem",
              }}
            >
              <div
                style={{
                  color: FALLBACK_RED,
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  marginBottom: "0.5rem",
                }}
              >
                {error.name || "Error"}
              </div>
              <div
                style={{
                  color: FALLBACK_FG,
                  fontSize: "0.875rem",
                  lineHeight: 1.5,
                  marginBottom: error.stack ? "0.75rem" : 0,
                  wordBreak: "break-word",
                }}
              >
                {error.message || "(pas de message)"}
              </div>
              {error.stack && (
                <details>
                  <summary
                    style={{
                      cursor: "pointer",
                      color: FALLBACK_GOLD,
                      fontSize: "0.75rem",
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                    }}
                  >
                    Stack trace
                  </summary>
                  <pre
                    style={{
                      marginTop: "0.75rem",
                      padding: "0.75rem",
                      background: "#0f0f23",
                      border: `1px solid ${FALLBACK_BORDER}`,
                      borderRadius: "0.375rem",
                      color: FALLBACK_DIM,
                      fontSize: "0.75rem",
                      lineHeight: 1.5,
                      overflow: "auto",
                      maxHeight: "240px",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      fontFamily:
                        "'JetBrains Mono', 'SFMono-Regular', Consolas, monospace",
                    }}
                  >
                    {error.stack}
                  </pre>
                </details>
              )}
              {this.state.errorInfo?.componentStack && (
                <details style={{ marginTop: "0.5rem" }}>
                  <summary
                    style={{
                      cursor: "pointer",
                      color: FALLBACK_GOLD,
                      fontSize: "0.75rem",
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                    }}
                  >
                    Component stack
                  </summary>
                  <pre
                    style={{
                      marginTop: "0.75rem",
                      padding: "0.75rem",
                      background: "#0f0f23",
                      border: `1px solid ${FALLBACK_BORDER}`,
                      borderRadius: "0.375rem",
                      color: FALLBACK_DIM,
                      fontSize: "0.75rem",
                      lineHeight: 1.5,
                      overflow: "auto",
                      maxHeight: "240px",
                      whiteSpace: "pre-wrap",
                      fontFamily:
                        "'JetBrains Mono', 'SFMono-Regular', Consolas, monospace",
                    }}
                  >
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}
            </div>
          )}

          <details style={{ marginBottom: "1.5rem" }}>
            <summary
              style={{
                cursor: "pointer",
                color: FALLBACK_GOLD,
                fontSize: "0.6875rem",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                fontWeight: 600,
              }}
            >
              Diagnostic context
            </summary>
            <dl
              style={{
                marginTop: "0.75rem",
                fontFamily:
                  "'JetBrains Mono', 'SFMono-Regular', Consolas, monospace",
                fontSize: "0.75rem",
                color: FALLBACK_DIM,
                lineHeight: 1.6,
                margin: 0,
              }}
            >
              <DiagRow label="path" value={diag.pathname} />
              <DiagRow label="timestamp" value={diag.timestamp} />
              <DiagRow
                label="localStorage"
                value={diag.hasLocalStorage ? "available" : "unavailable"}
              />
              <DiagRow
                label="supabase session"
                value={diag.hasSession ? "present" : "absent"}
              />
              <DiagRow label="userAgent" value={diag.userAgent} />
            </dl>
          </details>

          <div
            style={{
              display: "flex",
              gap: "0.75rem",
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={() => void safeSignOutAndRedirect()}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "0.75rem 1.25rem",
                background: FALLBACK_GOLD,
                color: FALLBACK_BG,
                border: "none",
                borderRadius: "0.375rem",
                fontSize: "0.875rem",
                fontWeight: 600,
                cursor: "pointer",
                minHeight: "2.75rem",
              }}
            >
              <LogOut size={16} />
              Reset session &amp; accueil
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "0.75rem 1.25rem",
                background: "transparent",
                color: FALLBACK_FG,
                border: `1px solid ${FALLBACK_BORDER}`,
                borderRadius: "0.375rem",
                fontSize: "0.875rem",
                fontWeight: 500,
                cursor: "pointer",
                minHeight: "2.75rem",
              }}
            >
              <RotateCcw size={16} />
              Recharger la page
            </button>
          </div>
        </div>
      </div>
    );
  }
}

function DiagRow({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: "flex",
        gap: "0.75rem",
        padding: "0.25rem 0",
        borderBottom: "1px dashed rgba(148,163,184,0.15)",
      }}
    >
      <dt
        style={{
          width: "9rem",
          flexShrink: 0,
          color: FALLBACK_GOLD,
          fontSize: "0.6875rem",
          letterSpacing: "0.05em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </dt>
      <dd style={{ margin: 0, wordBreak: "break-word", flex: 1 }}>{value}</dd>
    </div>
  );
}

export default ErrorBoundary;
